<template>
  <div class="pmc">
    <!-- Calendario -->
    <section class="pmc__calendar">
      <header class="pmc__cal-header">
        <div class="pmc__cal-nav">
          <button class="pmc__nav-btn" @click="shiftMonth(-1)"><span class="mdi mdi-chevron-left"></span></button>
          <span class="pmc__cal-title">{{ MONTH_LABELS[month - 1] }} {{ year }}</span>
          <button class="pmc__nav-btn" @click="shiftMonth(1)"><span class="mdi mdi-chevron-right"></span></button>
        </div>
        <button class="pmc__today-btn" @click="goToday">Hoy</button>
      </header>

      <div class="pmc__weekdays">
        <span v-for="d in WEEKDAYS" :key="d">{{ d }}</span>
      </div>

      <div class="pmc__grid">
        <div
          v-for="cell in cells"
          :key="cell.key"
          class="pmc__day"
          :class="{ 'pmc__day--muted': !cell.inMonth, 'pmc__day--today': cell.isToday }"
          @click="onDayClick(cell)"
        >
          <span class="pmc__day-number" :class="{ 'pmc__day-number--today': cell.isToday }">{{ cell.day }}</span>
          <div class="pmc__pills">
            <div
              v-for="item in cell.visibleItems"
              :key="item.id + item.startAt"
              class="pmc__pill"
              :class="{ 'pmc__pill--projected': item.projected }"
              :style="{ background: item.color }"
              :title="pillTooltip(item)"
              @click.stop="openDetail(item.id)"
            >
              {{ item.title }}
            </div>
            <div v-if="cell.extraCount > 0" class="pmc__pill pmc__pill--more" @click.stop="expandDay(cell)">
              +{{ cell.extraCount }} más
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Panel de gestión -->
    <aside class="pmc__panel">
      <!-- Modo A: resumen -->
      <div v-if="mode === 'summary'" class="pmc__summary">
        <div class="pmc__metrics">
          <div class="pmc__metric">
            <span class="pmc__metric-value">{{ monthData.summary?.total ?? 0 }}</span>
            <span class="pmc__metric-label">Programados</span>
          </div>
          <div class="pmc__metric">
            <span class="pmc__metric-value">{{ monthData.summary?.completionPercent ?? 0 }}%</span>
            <span class="pmc__metric-label">Cumplimiento</span>
          </div>
          <div class="pmc__metric">
            <span class="pmc__metric-value">${{ formatMoney(monthData.summary?.estimatedCost) }}</span>
            <span class="pmc__metric-label">Costo estimado</span>
          </div>
        </div>

        <div class="pmc__upcoming">
          <span class="pmc__section-label">Próximos mantenimientos</span>
          <div v-if="!monthData.upcoming?.length" class="pmc__empty">Sin mantenimientos próximos</div>
          <button
            v-for="item in monthData.upcoming"
            :key="item.id"
            class="pmc__upcoming-row"
            @click="openDetail(item.id)"
          >
            <span class="pmc__upcoming-date">{{ formatShortDate(item.startAt) }}</span>
            <span class="pmc__upcoming-title">{{ item.title }}</span>
            <span class="pmc__upcoming-tag" :style="{ background: INFRA_COLORS[item.infraType] }">{{ INFRA_LABELS[item.infraType] }}</span>
          </button>
        </div>

        <button class="pmc__add-btn" @click="openCreate(null)"><span class="mdi mdi-plus"></span>Programar mantenimiento</button>

        <p class="pmc__hint">Vista anual y línea de finanzas llegan en una siguiente iteración.</p>
      </div>

      <!-- Modo B: detalle -->
      <div v-else-if="mode === 'detail' && detail" class="pmc__detail">
        <button class="pmc__back" @click="mode = 'summary'"><span class="mdi mdi-arrow-left"></span> Volver</button>

        <h3 class="pmc__detail-title">{{ detail.title }}</h3>
        <div class="pmc__detail-tags">
          <span class="pmc__tag" :style="{ background: INFRA_COLORS[detail.infraType] }">{{ INFRA_LABELS[detail.infraType] }}</span>
          <span class="pmc__status" :class="`pmc__status--${detail.status}`">{{ STATUS_LABELS[detail.status] }}</span>
        </div>

        <div class="pmc__detail-block">
          <div class="pmc__field"><span class="pmc__field-label">Zona</span><span>{{ detail.zone?.name || '—' }}</span></div>
          <div class="pmc__field"><span class="pmc__field-label">Nave</span><span>{{ detail.plot?.name || '—' }}</span></div>
          <div class="pmc__field"><span class="pmc__field-label">Asignado</span><span>{{ detail.technician?.name || detail.externalProvider || '—' }}</span></div>
        </div>

        <div class="pmc__detail-block">
          <div class="pmc__field"><span class="pmc__field-label">Fecha</span><span>{{ formatFullDate(detail.startAt) }}</span></div>
          <div class="pmc__field"><span class="pmc__field-label">Duración</span><span>{{ detail.durationHours ? `${detail.durationHours}h` : '—' }}</span></div>
          <div v-if="detail.isRecurring" class="pmc__field"><span class="pmc__field-label">Frecuencia</span><span>{{ FREQUENCY_LABELS[detail.frequency] }}</span></div>
        </div>

        <div class="pmc__detail-block">
          <div class="pmc__field pmc__field--block"><span class="pmc__field-label">Descripción</span><span>{{ detail.description || '—' }}</span></div>
          <div class="pmc__field"><span class="pmc__field-label">Costo estimado</span><span>${{ formatMoney(detail.estimatedCost) }} {{ detail.currency }}</span></div>
        </div>

        <div class="pmc__detail-actions">
          <button
            v-if="detail.status !== 'COMPLETED' && detail.status !== 'CANCELLED'"
            class="pmc__action pmc__action--primary"
            @click="onComplete"
          >Marcar como completado</button>
          <button
            v-if="detail.status !== 'COMPLETED' && detail.status !== 'CANCELLED'"
            class="pmc__action"
            @click="openCreate(detail)"
          >Editar</button>
          <button
            v-if="detail.status !== 'CANCELLED' && detail.status !== 'COMPLETED'"
            class="pmc__action pmc__action--danger"
            @click="onCancel"
          >Cancelar mantenimiento</button>
          <button class="pmc__action" @click="onGenerateTicket">Generar ticket correctivo</button>
        </div>
        <p v-if="!detail.plotId" class="pmc__hint pmc__hint--warn">Este mantenimiento no tiene una nave asociada, así que no se puede generar ticket automáticamente ni manualmente. Edítalo para asignarle una.</p>
      </div>

      <!-- Modo C: crear/editar -->
      <div v-else-if="mode === 'form'" class="pmc__form">
        <button class="pmc__back" @click="mode = detail ? 'detail' : 'summary'"><span class="mdi mdi-arrow-left"></span> Volver</button>
        <h3 class="pmc__detail-title">{{ form.id ? 'Editar mantenimiento' : 'Programar mantenimiento' }}</h3>

        <span class="pmc__section-label">Identificación</span>
        <input v-model="form.title" class="pmc__input" placeholder="Nombre del mantenimiento" />
        <select v-model="form.infraType" class="pmc__input">
          <option v-for="k in Object.keys(INFRA_LABELS)" :key="k" :value="k">{{ INFRA_LABELS[k] }}</option>
        </select>
        <select v-model="form.zoneId" class="pmc__input">
          <option :value="null">Sin zona específica</option>
          <option v-for="z in zones" :key="z.id" :value="z.id">{{ z.name }}</option>
        </select>
        <textarea v-model="form.description" class="pmc__input pmc__textarea" rows="2" placeholder="Descripción del trabajo"></textarea>

        <span class="pmc__section-label">Programación</span>
        <input v-model="form.startDate" type="date" class="pmc__input" />
        <input v-model="form.startTime" type="time" class="pmc__input" />
        <input v-model.number="form.durationHours" type="number" min="0" step="0.5" class="pmc__input" placeholder="Duración estimada (horas)" />
        <label class="pmc__checkbox"><input type="checkbox" v-model="form.isRecurring" /> Mantenimiento recurrente</label>
        <template v-if="form.isRecurring">
          <select v-model="form.frequency" class="pmc__input">
            <option v-for="k in Object.keys(FREQUENCY_LABELS)" :key="k" :value="k">{{ FREQUENCY_LABELS[k] }}</option>
          </select>
          <input v-model="form.recurrenceEndAt" type="date" class="pmc__input" placeholder="Fin de la recurrencia (opcional)" />
        </template>

        <span class="pmc__section-label">Asignación</span>
        <select v-model="form.technicianId" class="pmc__input">
          <option :value="null">Sin técnico interno</option>
          <option v-for="t in props.technicians" :key="t.id" :value="t.id">{{ t.name }}</option>
        </select>
        <input v-model="form.externalProvider" class="pmc__input" placeholder="Proveedor externo (opcional)" />

        <span class="pmc__section-label">Nave relacionada</span>
        <p class="pmc__hint">Opcional. Solo necesaria si quieres que se pueda generar un ticket (automático o manual) desde este mantenimiento.</p>
        <select v-model="form.plotId" class="pmc__input">
          <option :value="null">Sin nave asociada</option>
          <option v-for="p in props.plots" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>

        <span class="pmc__section-label">Costo y documentación</span>
        <div class="pmc__cost-row">
          <input v-model.number="form.estimatedCost" type="number" min="0" class="pmc__input" placeholder="Costo estimado" />
          <select v-model="form.currency" class="pmc__input pmc__input--currency">
            <option value="MXN">MXN</option>
            <option value="USD">USD</option>
          </select>
        </div>
        <textarea v-model="form.notes" class="pmc__input pmc__textarea" rows="2" placeholder="Notas adicionales"></textarea>
        <label class="pmc__checkbox"><input type="checkbox" v-model="form.autoGenerateTicket" /> Generar ticket automáticamente en la fecha programada</label>

        <button class="pmc__new-btn" :disabled="!form.title || !form.infraType || !form.startDate" @click="onSubmitForm">
          {{ form.id ? 'Guardar cambios' : 'Programar mantenimiento' }}
        </button>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import {
  getParkZones,
  getScheduledMaintenanceMonth,
  getScheduledMaintenanceDetail,
  createScheduledMaintenance,
  updateScheduledMaintenance,
  cancelScheduledMaintenance,
  completeScheduledMaintenance,
  generateTicketFromScheduledMaintenance,
} from '@/services/maintenance'

const props = defineProps({
  parkId: { type: String, required: true },
  technicians: { type: Array, default: () => [] },
  plots: { type: Array, default: () => [] },
})
const emit = defineEmits(['open-ticket'])

const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']
const MONTH_LABELS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

const INFRA_LABELS = {
  ELECTRICAL: 'Eléctrico', HYDRAULIC: 'Hidráulico', HVAC: 'HVAC',
  FIRE_SYSTEMS: 'Contra incendios', COMMON_AREAS: 'Áreas comunes', OTHER: 'Otro',
}
const INFRA_COLORS = {
  ELECTRICAL: '#3b82f6', HYDRAULIC: '#10b981', HVAC: '#f0a500',
  FIRE_SYSTEMS: '#e84545', COMMON_AREAS: '#8b5cf6', OTHER: '#64748b',
}
const FREQUENCY_LABELS = {
  WEEKLY: 'Semanal', BIWEEKLY: 'Quincenal', MONTHLY: 'Mensual', BIMONTHLY: 'Bimestral',
  QUARTERLY: 'Trimestral', SEMIANNUAL: 'Semestral', ANNUAL: 'Anual',
}
const STATUS_LABELS = {
  SCHEDULED: 'Programado', CONFIRMED: 'Confirmado', IN_PROGRESS: 'En proceso',
  COMPLETED: 'Completado', CANCELLED: 'Cancelado',
}

const now = new Date()
const year = ref(now.getFullYear())
const month = ref(now.getMonth() + 1) // 1-12

const monthData = ref({ items: [], summary: null, upcoming: [] })
const zones = ref([])

const mode = ref('summary') // summary | detail | form
const detail = ref(null)
const form = reactive(emptyForm())

function emptyForm() {
  return {
    id: null, title: '', infraType: 'ELECTRICAL', description: '', zoneId: null, plotId: null,
    startDate: '', startTime: '09:00', durationHours: 1, isRecurring: false, frequency: 'MONTHLY',
    recurrenceEndAt: '', technicianId: null, externalProvider: '', estimatedCost: null, currency: 'MXN',
    notes: '', autoGenerateTicket: true,
  }
}

async function loadMonth() {
  monthData.value = await getScheduledMaintenanceMonth(props.parkId, year.value, month.value)
}

onMounted(async () => {
  zones.value = await getParkZones(props.parkId)
  await loadMonth()
})

watch([year, month], loadMonth)

function shiftMonth(delta) {
  let m = month.value + delta
  let y = year.value
  if (m < 1) { m = 12; y -= 1 }
  if (m > 12) { m = 1; y += 1 }
  month.value = m
  year.value = y
}

function goToday() {
  year.value = now.getFullYear()
  month.value = now.getMonth() + 1
}

const expandedDays = ref({})

function expandDay(cell) {
  expandedDays.value = { ...expandedDays.value, [cell.key]: true }
}

const cells = computed(() => {
  const firstOfMonth = new Date(year.value, month.value - 1, 1)
  // Lunes = 0 ... Domingo = 6
  const firstWeekday = (firstOfMonth.getDay() + 6) % 7
  const daysInMonth = new Date(year.value, month.value, 0).getDate()
  const prevDaysInMonth = new Date(year.value, month.value - 1, 0).getDate()

  const itemsByDay = {}
  for (const item of monthData.value.items || []) {
    const d = new Date(item.startAt)
    const key = d.getDate()
    if (!itemsByDay[key]) itemsByDay[key] = []
    itemsByDay[key].push(item)
  }

  const todayKey = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`

  const list = []
  for (let i = 0; i < firstWeekday; i++) {
    const day = prevDaysInMonth - firstWeekday + i + 1
    list.push({ key: `prev-${day}`, day, inMonth: false, isToday: false, visibleItems: [], extraCount: 0, date: null })
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const items = itemsByDay[day] || []
    const key = `${year.value}-${month.value}-${day}`
    const expanded = !!expandedDays.value[key]
    const visibleItems = expanded ? items : items.slice(0, 2)
    const extraCount = expanded ? 0 : Math.max(0, items.length - 2)
    list.push({
      key, day, inMonth: true, isToday: key === todayKey,
      visibleItems, extraCount, date: new Date(year.value, month.value - 1, day),
    })
  }
  let nextDay = 1
  while (list.length % 7 !== 0) {
    list.push({ key: `next-${nextDay}`, day: nextDay, inMonth: false, isToday: false, visibleItems: [], extraCount: 0, date: null })
    nextDay++
  }
  return list
})

function pillTooltip(item) {
  const parts = [item.title]
  if (item.technician?.name) parts.push(`Técnico: ${item.technician.name}`)
  else if (item.externalProvider) parts.push(`Proveedor: ${item.externalProvider}`)
  if (item.zone?.name) parts.push(`Zona: ${item.zone.name}`)
  if (item.durationHours) parts.push(`Duración: ${item.durationHours}h`)
  return parts.join(' · ')
}

function onDayClick(cell) {
  if (!cell.inMonth) return
  const isPast = cell.date < new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (isPast) return
  if (!cell.visibleItems.length && cell.extraCount === 0) openCreate(null, cell.date)
}

async function openDetail(id) {
  detail.value = await getScheduledMaintenanceDetail(id)
  mode.value = 'detail'
}

function openCreate(existing, presetDate) {
  if (existing) {
    const d = new Date(existing.startAt)
    Object.assign(form, {
      id: existing.id, title: existing.title, infraType: existing.infraType, description: existing.description || '',
      zoneId: existing.zoneId, plotId: existing.plotId,
      startDate: d.toISOString().slice(0, 10), startTime: d.toISOString().slice(11, 16),
      durationHours: existing.durationHours, isRecurring: existing.isRecurring, frequency: existing.frequency || 'MONTHLY',
      recurrenceEndAt: existing.recurrenceEndAt ? new Date(existing.recurrenceEndAt).toISOString().slice(0, 10) : '',
      technicianId: existing.technicianId, externalProvider: existing.externalProvider || '',
      estimatedCost: existing.estimatedCost, currency: existing.currency || 'MXN', notes: existing.notes || '',
      autoGenerateTicket: existing.autoGenerateTicket,
    })
  } else {
    Object.assign(form, emptyForm())
    if (presetDate) form.startDate = presetDate.toISOString().slice(0, 10)
  }
  mode.value = 'form'
}

async function onSubmitForm() {
  const payload = {
    title: form.title,
    infraType: form.infraType,
    description: form.description || null,
    zoneId: form.zoneId || null,
    plotId: form.plotId || null,
    startAt: new Date(`${form.startDate}T${form.startTime || '09:00'}`).toISOString(),
    durationHours: form.durationHours || null,
    isRecurring: form.isRecurring,
    frequency: form.isRecurring ? form.frequency : null,
    recurrenceEndAt: form.isRecurring && form.recurrenceEndAt ? new Date(form.recurrenceEndAt).toISOString() : null,
    technicianId: form.technicianId || null,
    externalProvider: form.externalProvider || null,
    estimatedCost: form.estimatedCost ?? null,
    currency: form.currency,
    notes: form.notes || null,
    autoGenerateTicket: form.autoGenerateTicket,
  }

  if (form.id) {
    await updateScheduledMaintenance(form.id, payload)
  } else {
    await createScheduledMaintenance(props.parkId, payload)
  }
  await loadMonth()
  mode.value = 'summary'
}

async function onComplete() {
  await completeScheduledMaintenance(detail.value.id)
  await loadMonth()
  mode.value = 'summary'
}

async function onCancel() {
  const reason = prompt('Motivo de la cancelación:')
  if (reason === null) return
  await cancelScheduledMaintenance(detail.value.id, reason)
  await loadMonth()
  mode.value = 'summary'
}

async function onGenerateTicket() {
  try {
    const ticket = await generateTicketFromScheduledMaintenance(detail.value.id)
    emit('open-ticket', ticket.id)
  } catch (e) {
    alert(e.message)
  }
}

function formatMoney(n) {
  if (!n) return '0'
  return Math.round(n).toLocaleString('es-MX')
}

function formatShortDate(iso) {
  const d = new Date(iso)
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })
}

function formatFullDate(iso) {
  const d = new Date(iso)
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
</script>

<style scoped>
.pmc {
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 1rem;
  padding: 1rem 1.5rem;
  overflow: hidden;
}

.pmc__calendar {
  flex: 0 0 60%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  min-height: 0;
}

.pmc__cal-header {
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.pmc__cal-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pmc__nav-btn {
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  font-size: 18px;
  display: flex;
}

.pmc__nav-btn:hover { color: #243855; }

.pmc__cal-title {
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 14px;
  color: #243855;
  min-width: 140px;
  text-align: center;
}

.pmc__today-btn {
  background: none;
  border: none;
  color: #3b82f6;
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
}

.pmc__weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 0.5rem 0.5rem 0;
  flex-shrink: 0;
}

.pmc__weekdays span {
  text-align: center;
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
}

.pmc__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 80px;
  padding: 0.25rem 0.5rem 0.5rem;
  overflow-y: auto;
  flex: 1;
}

.pmc__day {
  border: 1px solid #f1f5f9;
  padding: 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  cursor: pointer;
  overflow: hidden;
}

.pmc__day:hover {
  background: #f8fafc;
}

.pmc__day--muted {
  background: #fafbfc;
  cursor: default;
}

.pmc__day--muted:hover {
  background: #fafbfc;
}

.pmc__day--muted .pmc__day-number {
  color: #cbd5e1;
}

.pmc__day-number {
  font-family: var(--font-body);
  font-size: 11px;
  color: #64748b;
}

.pmc__day-number--today {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #3b82f6;
  color: #ffffff;
}

.pmc__pills {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.pmc__pill {
  height: 16px;
  line-height: 16px;
  border-radius: 8px;
  padding: 0 6px;
  font-size: 9px;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.pmc__pill--more {
  background: #e2e8f0 !important;
  color: #64748b;
}

.pmc__panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem;
  overflow-y: auto;
  gap: 0.6rem;
}

.pmc__metrics {
  display: flex;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.pmc__metric {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.6rem 0.25rem;
  border-right: 1px solid #e2e8f0;
}
.pmc__metric:last-child { border-right: none; }

.pmc__metric-value {
  font-family: var(--font-mono);
  font-size: 16px;
  font-weight: 600;
  color: #243855;
}

.pmc__metric-label {
  font-family: var(--font-body);
  font-size: 10px;
  color: #94a3b8;
  text-align: center;
}

.pmc__section-label {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.pmc__empty {
  font-family: var(--font-body);
  font-size: 12px;
  color: #94a3b8;
  padding: 0.5rem 0;
}

.pmc__pill--projected { opacity: 0.6; outline: 1px dashed rgba(255, 255, 255, 0.7); outline-offset: -2px; }

.pmc__upcoming {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.pmc__upcoming-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 48px;
  background: none;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  text-align: left;
  padding: 0 0.25rem;
}

.pmc__upcoming-row:hover { background: #f8fafc; }

.pmc__upcoming-date {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #64748b;
  flex-shrink: 0;
  width: 48px;
}

.pmc__upcoming-title {
  font-family: var(--font-body);
  font-size: 12px;
  color: #243855;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pmc__upcoming-tag {
  font-size: 9px;
  color: #ffffff;
  padding: 2px 6px;
  border-radius: 8px;
  flex-shrink: 0;
}

.pmc__new-btn {
  height: 38px;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  margin-top: auto;
}

.pmc__new-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pmc__new-btn:hover:not(:disabled) { background: #2563eb; }

/* Botón compacto del resumen lateral */
.pmc__add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  align-self: flex-start;
  height: 30px;
  padding: 0 12px;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  margin-top: auto;
}
.pmc__add-btn .mdi { font-size: 14px; line-height: 1; }
.pmc__add-btn:hover { background: #2563eb; }

.pmc__hint {
  font-family: var(--font-body);
  font-size: 10.5px;
  color: #94a3b8;
  line-height: 1.4;
}

.pmc__hint--warn {
  color: #d97706;
}

.pmc__back {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  color: #64748b;
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  align-self: flex-start;
}

.pmc__detail-title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 16px;
  color: #243855;
  margin: 0;
}

.pmc__detail-tags {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.pmc__tag {
  font-size: 10px;
  color: #ffffff;
  padding: 2px 8px;
  border-radius: 10px;
}

.pmc__status {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
}

.pmc__status--SCHEDULED { background: #dbeafe; color: #1d4ed8; }
.pmc__status--CONFIRMED { background: #dcfce7; color: #15803d; }
.pmc__status--IN_PROGRESS { background: #fef3c7; color: #b45309; }
.pmc__status--COMPLETED { background: #d1fae5; color: #065f46; }
.pmc__status--CANCELLED { background: #e2e8f0; color: #64748b; }

.pmc__detail-block {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  border-top: 1px solid #f1f5f9;
  padding-top: 0.5rem;
}

.pmc__field {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #243855;
  min-width: 100px;
}

.pmc__field--block {
  width: 100%;
}

.pmc__field-label {
  font-size: 10px;
  color: #94a3b8;
  text-transform: uppercase;
}

.pmc__detail-actions {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.pmc__action {
  height: 34px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #243855;
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
}

.pmc__action:hover { background: #f8fafc; }
.pmc__action--primary { background: #3b82f6; border-color: #3b82f6; color: #ffffff; }
.pmc__action--primary:hover { background: #2563eb; }
.pmc__action--danger { color: #dc2626; border-color: #fecaca; }
.pmc__action--danger:hover { background: #fef2f2; }

.pmc__form {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.pmc__input {
  height: 34px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  padding: 0 0.6rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #243855;
  width: 100%;
  box-sizing: border-box;
}

.pmc__textarea {
  height: auto;
  padding: 0.4rem 0.6rem;
  resize: vertical;
}

.pmc__cost-row {
  display: flex;
  gap: 0.4rem;
}

.pmc__input--currency {
  flex: 0 0 90px;
}

.pmc__checkbox {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #243855;
}
</style>